import React from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import styled from 'styled-components';

import { useGlobalization } from '@Translations';
import { WEAVE_FONT } from '@Utils/fonts';

const ToggleRoot = styled.div`
	display: inline-flex;
	border: 1px solid #111827;
	border-radius: 2px;
	overflow: hidden;
	flex-shrink: 0;
	align-self: flex-start;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
	font-family: ${WEAVE_FONT};
	font-size: 0.875rem;
	font-weight: 500;
	padding: 7px 24px;
	border: none;
	cursor: pointer;
	background: ${({ $active }) => ($active ? '#111827' : '#ffffff')};
	color: ${({ $active }) => ($active ? '#ffffff' : '#111827')};
	transition: background 0.15s, color 0.15s;

	&:hover {
		background: ${({ $active }) => ($active ? '#111827' : '#f3f4f6')};
	}
`;

interface ModelResultsSelectionCardProps {
	selectedTab: 'assets' | 'groups';
	setSelectedTab: (tab: 'assets' | 'groups') => void;
	onNavigateToGradeSetup?: () => void;
	onLearnMore?: () => void;
}

export const ModelResultsSelectionCard = ({
	selectedTab,
	setSelectedTab,
	onNavigateToGradeSetup,
	onLearnMore,
}: ModelResultsSelectionCardProps) => {
	const { t } = useGlobalization();

	return (
		<Stack direction="column" spacing={1.5}>
			<Stack direction="column" spacing={0.5}>
				<Typography
					sx={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827', lineHeight: 1.4, fontFamily: WEAVE_FONT }}>
					{t('Model Results')}
				</Typography>
				<Typography sx={{ fontSize: '0.8125rem', color: '#6b7280', fontFamily: WEAVE_FONT }}>
					{t(
						// eslint-disable-next-line prettier/prettier
						'Results show each asset\'s failure probability and estimated remaining life. Use Assets view to inspect individual assets and their failure grade,',
					)}
					<br />
					{t(
						'or switch to Groups to compare deterioration curves and see when each group crosses the failure threshold.',
					)}{' '}
					<Link
						sx={{ cursor: 'pointer', textDecoration: 'none', fontSize: '0.8125rem' }}
						component="button"
						onClick={onLearnMore}>
						{t('Learn more')}
					</Link>
				</Typography>
			</Stack>
			<Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', pr: '2px' }}>
				<ToggleRoot>
					<ToggleBtn
						type="button"
						$active={selectedTab === 'assets'}
						aria-pressed={selectedTab === 'assets'}
						onClick={() => setSelectedTab('assets')}>
						{t('Assets')}
					</ToggleBtn>
					<ToggleBtn
						type="button"
						$active={selectedTab === 'groups'}
						aria-pressed={selectedTab === 'groups'}
						onClick={() => setSelectedTab('groups')}>
						{t('Groups')}
					</ToggleBtn>
				</ToggleRoot>
				{onNavigateToGradeSetup && selectedTab === 'assets' && (
					<Link
						component="button"
						onClick={onNavigateToGradeSetup}
						sx={{
							fontSize: '0.875rem',
							fontFamily: WEAVE_FONT,
							textDecoration: 'none',
							cursor: 'pointer',
							color: 'primary.main',
						}}>
						{t('Edit Grades')}
					</Link>
				)}
			</Box>
		</Stack>
	);
};
